'use client';

import { useAuiState } from '@assistant-ui/react';
import { type SessionSummary } from 'conversational-search-client';
import { telemetryIncrementAction } from 'core-events/actions';
import { createContext, useContext, useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';

import { Toast, ToastProvider } from '@videoblocks/design-system';

import { useRateLimit } from '../_hooks/useRateLimit';
import { isBrowseLimitError, isRateLimitError, parseRetryAfter } from '../_utils/constants';

// ── Constants ────────────────────────────────────────────────────────

export const DEFAULT_DOWNLOAD_ERROR_MESSAGE = 'Download error has occurred';

const DOWNLOAD_ERROR_TOAST_DURATION_MS = 5000;

// ── Context ──────────────────────────────────────────────────────────

interface SessionContextValue {
    sessionId: string | undefined;
    sessions: SessionSummary[];
    currentTitle: string;
    hasReachedLimit: boolean;
    isRateLimited: boolean;
    rateLimitResetsAt: Date | null;
    canDownloadVideo: boolean;
    canDownloadAudio: boolean;
    supportsAudio: boolean;
    downloadError: string | null;
    setDownloadError: (message: string | null) => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function useChatSessionContext(): SessionContextValue {
    const context = useContext(SessionContext);
    if (!context) {
        throw new Error('useChatSessionContext must be used within a ChatSessionProvider');
    }
    return context;
}

// ── Hooks ────────────────────────────────────────────────────────────

// The assistant-ui metadata shape is not statically typed by the library;
// we cast to this interface to express the shape we expect from the stream.
interface AssistantStreamMetadata {
    custom?: Record<string, unknown>;
}

function useStreamMetadataField(field: 'sessionId' | 'conversationTitle'): string | undefined {
    return useAuiState(({ thread }) => {
        const lastAssistant = thread.messages.findLast((m) => m.role === 'assistant');
        const value = (lastAssistant?.metadata as AssistantStreamMetadata | undefined)?.custom?.[field];
        return typeof value === 'string' ? value : undefined;
    });
}

function useNewChatSession(existingSessionId?: string): SessionSummary | undefined {
    const [session, setSession] = useState<SessionSummary | undefined>(undefined);
    const streamSessionId = useStreamMetadataField('sessionId');
    const streamTitle = useStreamMetadataField('conversationTitle');

    // Captures session once when the backend assigns it to a new chat.
    // Uses window.history.pushState instead of Next's router to avoid triggering
    // a server re-render that would remount ChatRuntimeProvider (keyed on sessionId)
    // and kill the active stream.
    useEffect(() => {
        if (existingSessionId || session || !streamSessionId) return;

        const url = new URL(window.location.href);
        url.pathname = `/ai/conv-search/${streamSessionId}`;
        // Drop the consumed `prompt` param so reloading or navigating back doesn't re-fire the search.
        url.searchParams.delete('prompt');
        window.history.pushState(window.history.state, '', url.toString());

        // eslint-disable-next-line react-hooks/set-state-in-effect -- capture-once; `session` guard prevents re-runs
        setSession({
            id: streamSessionId,
            name: streamTitle ?? 'New Search',
            lastActivity: new Date().toISOString(),
            messageCount: 1,
        });
    }, [existingSessionId, session, streamSessionId, streamTitle]);

    return session;
}

/** Detects BROWSE_LIMIT_EXCEEDED in the last assistant message's error status. */
function useBrowseLimitReached(): boolean {
    return useAuiState(({ thread }) => {
        const lastAssistant = thread.messages.findLast((m) => m.role === 'assistant');
        if (lastAssistant?.status?.type !== 'incomplete' || lastAssistant.status.reason !== 'error') return false;
        return isBrowseLimitError(lastAssistant.status.error);
    });
}

/**
 * Detects RATE_LIMIT_EXCEEDED in the last assistant message's error status and
 * returns the retryAfter value in seconds, or null if not a rate limit error.
 */
function useRateLimitRetryAfter(): number | null {
    return useAuiState(({ thread }) => {
        const lastAssistant = thread.messages.findLast((m) => m.role === 'assistant');
        if (lastAssistant?.status?.type !== 'incomplete' || lastAssistant.status.reason !== 'error') return null;
        if (!isRateLimitError(lastAssistant.status.error)) return null;
        return parseRetryAfter(lastAssistant.status.error);
    });
}

// ── Provider ─────────────────────────────────────────────────────────

interface SessionProviderProps {
    children: ReactNode;
    initialSession?: SessionSummary;
    initialSessions: SessionSummary[];
    canDownloadVideo?: boolean;
    canDownloadAudio?: boolean;
    supportsAudio?: boolean;
}

export function ChatSessionProvider({
    children,
    initialSession,
    initialSessions,
    canDownloadVideo = false,
    canDownloadAudio = false,
    supportsAudio = false,
}: SessionProviderProps): JSX.Element {
    const newChatSession = useNewChatSession(initialSession?.id);
    const currentSession = newChatSession ?? initialSession;

    // Log telemetry only for fresh sessions (no session query param / no initialSession)
    useEffect(() => {
        if (!initialSession) {
            void telemetryIncrementAction('conv-search.session.init');
        }
    }, [initialSession]);

    const sessionId = currentSession?.id;
    const currentTitle = currentSession?.name ?? 'New Search';

    const sessions = useMemo(() => {
        if (!currentSession) return initialSessions;

        const alreadyInList = initialSessions.some((session) => session.id === currentSession.id);
        return alreadyInList ? initialSessions : [currentSession, ...initialSessions];
    }, [initialSessions, currentSession]);

    const hasReachedLimit = useBrowseLimitReached();

    const { isRateLimited, resetsAt: rateLimitResetsAt, activateRateLimit } = useRateLimit();
    const retryAfterSeconds = useRateLimitRetryAfter();

    // When we detect a rate limit error in the stream, activate the localStorage-backed timer.
    useEffect(() => {
        if (retryAfterSeconds !== null) {
            activateRateLimit(Date.now() + retryAfterSeconds * 1000);
        }
    }, [retryAfterSeconds, activateRateLimit]);

    const [downloadError, setDownloadError] = useState<string | null>(null);

    const value = useMemo(
        () => ({
            sessionId,
            sessions,
            currentTitle,
            hasReachedLimit,
            isRateLimited,
            rateLimitResetsAt,
            canDownloadVideo,
            canDownloadAudio,
            supportsAudio,
            downloadError,
            setDownloadError,
        }),
        [
            sessionId,
            sessions,
            currentTitle,
            hasReachedLimit,
            isRateLimited,
            rateLimitResetsAt,
            canDownloadVideo,
            canDownloadAudio,
            supportsAudio,
            downloadError,
            setDownloadError,
        ],
    );

    return (
        <SessionContext value={value}>
            <ToastProvider>
                {children}
                <Toast
                    open={downloadError !== null}
                    onOpenChange={(open) => {
                        if (!open) setDownloadError(null);
                    }}
                    duration={DOWNLOAD_ERROR_TOAST_DURATION_MS}
                    title={downloadError ?? ''}
                    variant="error"
                    viewportClassName="fixed bottom-[var(--spacing-6)] right-[var(--spacing-6)] flex flex-col gap-[var(--spacing-2)] z-[1050] outline-none"
                />
            </ToastProvider>
        </SessionContext>
    );
}
